import {
  Activity,
  ArrowRightLeft,
  BarChart3,
  Bell,
  Book,
  BookOpen,
  Bot,
  Building,
  Building2,
  Calculator,
  Calendar,
  ClipboardCheck,
  Cog,
  Coins,
  Download,
  FileAudio,
  FileText,
  FileUp,
  Folders,
  Gauge,
  Globe,
  Headset,
  HeartPulse,
  KeyRound,
  LifeBuoy,
  type LucideIcon,
  Megaphone,
  MessageCircle,
  Percent,
  PhoneCall,
  PhoneOutgoing,
  Plus,
  ReceiptEuro,
  Rows3,
  Send,
  Server,
  Settings,
  Settings2,
  ShieldCheck,
  SwatchBook,
  Table2,
  Target,
  Unlink,
  Wallet,
  Webhook,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import { CheckSquareFilledIcon } from '@/components/icons/CheckSquareFilledIcon';
import { CheckSquareIcon } from '@/components/icons/CheckSquareIcon';
import { ChecksDoneFilledIcon } from '@/components/icons/ChecksDoneFilledIcon';
import { ChecksDoneIcon } from '@/components/icons/ChecksDoneIcon';
import { ClipboardCheckFilledIcon } from '@/components/icons/ClipboardCheckFilledIcon';
import { ClipboardCheckIcon } from '@/components/icons/ClipboardCheckIcon';
import { FolderFilledIcon } from '@/components/icons/FolderFilledIcon';
import { FolderIcon } from '@/components/icons/FolderIcon';
import { HomeFilledIcon } from '@/components/icons/HomeFilledIcon';
import { HomeIcon } from '@/components/icons/HomeIcon';
import { InboxFilledIcon } from '@/components/icons/InboxFilledIcon';
import { InboxIcon } from '@/components/icons/InboxIcon';
import { PaletteFilledIcon } from '@/components/icons/PaletteFilledIcon';
import { PaletteIcon } from '@/components/icons/PaletteIcon';
import { UserSearchFilledIcon } from '@/components/icons/UserSearchFilledIcon';
import { UserSearchIcon } from '@/components/icons/UserSearchIcon';
import { withoutCellPrefix } from '@/lib/global/market-routing';
import type { UserRole } from '@/types/domain/app';

export type NavIcon = LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;

/** Roles that can see the nav item. If omitted, visible to everyone. system-admin always sees all. */
export type NavItemVisibility = UserRole[];

/**
 * Hub roles that grant operator-context nav. Module roles are scope-agnostic;
 * operator vs client context is driven by the selected context, not the role.
 */
export const OPERATOR_ROLES: UserRole[] = [
  'customer-care-agent',
  'customer-care-admin',
  'finance-agent',
  'finance-admin',
  'data-ingestion-agent',
  'data-ingestion-admin',
  'analytics-agent',
  'analytics-admin',
  'campaigns-agent',
  'campaigns-admin',
  'ai-agents-agent',
  'ai-agents-admin',
  'finance-approver-1-admin',
  'finance-approver-2-admin',
  'refund-approver-1-admin',
  'refund-approver-2-admin',
  'settings-admin',
  'settings-viewer',
  'payout_approver_l1',
  'payout_approver_l2',
  // Legacy slugs retained during WorkOS role migration
  'operator-admin',
  'operator-ops',
  'operator-finance',
  'debt-type-analyst',
];
/** Hub roles that grant client-context nav (scope-agnostic module roles + legacy client roles) */
export const CLIENT_ROLES: UserRole[] = [
  'customer-care-agent',
  'customer-care-admin',
  'customer-care-client-agent',
  'customer-care-client-admin',
  'analytics-agent',
  'analytics-admin',
  'finance-agent',
  'finance-admin',
  'data-ingestion-agent',
  'data-ingestion-admin',
  'campaigns-agent',
  'campaigns-admin',
  'ai-agents-agent',
  'ai-agents-admin',
  // Legacy slugs retained during WorkOS role migration
  'client-admin',
  'client-analyst',
  'client-viewer',
];

/**
 * App path prefixes where debt type selection is hidden and auto-cleared.
 * These apps operate at operator/client level only (customer care, finances, user management).
 */
export const DEBT_TYPE_HIDDEN_PREFIXES: string[] = ['/app/customers', '/app/finances', '/users'];

export function shouldClearDebtType(pathname: string): boolean {
  return DEBT_TYPE_HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Analytics routes that require FGA all-clients (wildcard `*`) access.
 * Hidden in nav and redirected when `/users/me/clients` reports `all_clients: false`.
 * `/app/analytics/export/builder` is covered by the export prefix.
 */
export const ANALYTICS_REQUIRES_ALL_CLIENTS_ACCESS_PATHS: string[] = [
  '/app/analytics/customer-service',
  '/app/analytics/tag-accounts',
  '/app/analytics/explorer',
  '/app/analytics/export',
  '/app/analytics/ode-health-check',
];

/**
 * Settings routes that require FGA all-clients (wildcard `*`) access.
 * Hidden in nav and redirected when `/users/me/clients` reports `all_clients: false`.
 */
export const SETTINGS_REQUIRES_ALL_CLIENTS_ACCESS_PATHS: string[] = ['/settings/http-destinations'];

export interface AppNavItem {
  id?: string;
  name: string;
  to: string;
  icon: NavIcon;
  activeIcon?: NavIcon;
  /** When true, item is disabled with operator-level tooltip when viewed in operator context. */
  operatorDisabled?: boolean;
  /** When true, item shows a "Coming soon" badge. Disabled for everyone except system-admin. */
  comingSoon?: boolean;
  badge?: number | string;
  /** Marks the Settings item for the scope the context switcher is on; the sidebar puts a dot by it. */
  isSelectedScope?: boolean;
  visibility?: NavItemVisibility;
  /**
   * When true, item is hidden unless the user has FGA all-clients (wildcard `*`)
   * access (`/users/me/clients` envelope `all_clients: true`).
   */
  requiresAllClientsAccess?: boolean;
  action?: {
    icon: LucideIcon;
    to: string;
    label: string;
    /** Hide nav plus-action unless dual-gate ingestion settings write. */
    requiresIngestionSettingsWrite?: boolean;
  };
}

/**
 * Nav item after role/context resolution. `disabled` and `disabledReason` are
 * computed by `resolveNavGroups` and consumed by sidebar components — never
 * declared on item configs directly.
 */
export interface ResolvedAppNavItem extends AppNavItem {
  disabled?: boolean;
  /** Tooltip shown when disabled. Undefined = no tooltip (e.g. coming-soon items). */
  disabledReason?: string;
}

export interface AppNavGroup {
  label?: string;
  items: ResolvedAppNavItem[];
}

export interface AppNavigation {
  appName: string;
  groups: AppNavGroup[];
  comingSoon?: boolean;
  layout?: 'default' | 'compact'; // Sidebar style to use
}

/**
 * Resolve disabled state for nav items based on role/context.
 * - operatorDisabled + isOperator → disabled with operator-level tooltip
 * - comingSoon (and not system admin) → disabled, no tooltip (badge only)
 * - comingSoon + system admin → enabled, badge still shown
 */
function resolveNavGroups(groups: AppNavGroup[], isOperator: boolean, isSystemAdmin: boolean): AppNavGroup[] {
  return groups.map((group) => ({
    ...group,
    items: group.items.map((item): ResolvedAppNavItem => {
      if (isOperator && item.operatorDisabled) {
        return { ...item, disabled: true, disabledReason: 'This functionality is not available at operator level' };
      }
      if (item.comingSoon && !isSystemAdmin) {
        return { ...item, disabled: true };
      }
      return item;
    }),
  }));
}

/**
 * Settings pages editable at debt_type scope. Every page stays in the sidebar at
 * that scope; SettingsLayout renders the ones missing here read-only.
 * Per design decision D5: debt type overrides are selective per domain. Any domain
 * configurable via rule_fragments can have debt_type-level overrides.
 * Excluded: Organisation (operator/client data, not pure config), Authentication (no domain).
 * Debt Types is here for the selected debt type's own record, not a rule_fragments domain.
 */
export const DEBT_TYPE_SETTINGS_ROUTES = new Set([
  '/settings/debt-types',
  '/settings/branding', // theme domain
  '/settings/commission', // commercials domain
  '/settings/invoicing', // commercials domain
  '/settings/model-parameters', // commercials domain
  '/settings/communications', // communications domain
  '/settings/notifications', // communications domain
  '/settings/payments', // journey domain
  '/settings/operations', // journey domain
  '/settings/sftp', // connections domain
  '/settings/api-egress', // connections domain; client-scoped
]);

/**
 * Settings pages that read and write the operator's own record or config whatever the
 * context switcher says, so an edit made with a client selected would silently change
 * every client's.
 */
export const OPERATOR_ONLY_SETTINGS_ROUTES = new Set(['/settings/organisation', '/settings/accounting', '/settings/http-destinations']);

/** Names of the scopes in focus; the Settings items for those scopes are labelled with them. */
export interface SettingsScopeNames {
  operator?: string;
  client?: string;
  debtType?: string;
}

export function getAppNavigation(
  pathname: string,
  isOperator: boolean = false,
  isSystemAdmin: boolean = false,
  scopeNames: SettingsScopeNames = {}
): AppNavigation | null {
  const navigation = buildAppNavigation(withoutCellPrefix(pathname), scopeNames);
  if (!navigation) return null;
  return {
    ...navigation,
    groups: resolveNavGroups(navigation.groups, isOperator, isSystemAdmin),
  };
}

function buildAppNavigation(pathname: string, scopeNames: SettingsScopeNames): AppNavigation | null {
  // Files app
  if (pathname.startsWith('/app/ingestion')) {
    return {
      appName: 'Ingestion',
      groups: [
        {
          items: [
            {
              name: 'Overview',
              to: '/app/ingestion',
              icon: HomeIcon,
            },
          ],
        },
        {
          label: 'Data Ingestion',
          items: [
            {
              name: 'File Uploads',
              to: '/app/ingestion/file-upload',
              icon: FileUp,
            },
            {
              name: 'File Mappings',
              to: '/app/ingestion/configurations',
              icon: Cog,
              action: { icon: Plus, to: '/app/ingestion/configurations/add-v2', label: 'New Configuration' },
            },
            {
              name: 'Partner API',
              to: '/app/ingestion/api',
              icon: Webhook,
            },
          ],
        },
        {
          label: 'Settings',
          items: [
            {
              name: 'Ingestion alerts',
              to: '/app/ingestion/alerts',
              icon: Bell,
            },
            {
              name: 'SFTP',
              to: '/app/ingestion/sftp',
              icon: Server,
            },
          ],
        },
      ],
    };
  }

  // Customers app - compact sidebar layout
  if (pathname.startsWith('/app/customers')) {
    const navigation: AppNavigation = {
      appName: 'Customers',
      layout: 'compact',
      groups: [
        {
          items: [
            { id: 'customers', name: 'Accounts', to: '/app/customers/accounts', icon: UserSearchIcon, activeIcon: UserSearchFilledIcon },
            {
              id: 'task-feed',
              name: 'Tasks',
              to: '/app/customers/task-feed',
              icon: CheckSquareIcon,
              activeIcon: CheckSquareFilledIcon,
              visibility: ['operator-admin', 'operator-ops', 'customer-care-agent', 'customer-care-admin', 'settings-admin'],
            },
            {
              id: 'all-tasks',
              name: 'All Tasks',
              to: '/app/customers/all-tasks',
              icon: ChecksDoneIcon,
              activeIcon: ChecksDoneFilledIcon,
              visibility: ['operator-admin', 'customer-care-admin', 'settings-admin'],
            },
            {
              id: 'orphan-queue',
              name: 'Orphans',
              to: '/app/customers/orphan-queue',
              icon: Unlink,
              activeIcon: Unlink,
              visibility: ['operator-admin', 'operator-ops', 'customer-care-admin', 'settings-admin'],
            },

            {
              id: 'tickets',
              name: 'Tickets',
              to: '/app/customers/tickets',
              icon: ClipboardCheckIcon,
              activeIcon: ClipboardCheckFilledIcon,
              visibility: [...OPERATOR_ROLES, ...CLIENT_ROLES],
            },
          ],
        },
        {
          items: [
            {
              id: 'inbox',
              name: 'Inbox',
              to: '/app/customers/inbox',
              icon: InboxIcon,
              activeIcon: InboxFilledIcon,
              visibility: OPERATOR_ROLES,
            },
          ],
        },
      ],
    };
    return navigation;
  }

  // Campaigns app (renamed from Communications)
  if (pathname.startsWith('/app/campaigns')) {
    const navigation: AppNavigation = {
      appName: 'Campaigns',
      groups: [
        {
          items: [
            {
              name: 'Home',
              to: '/app/campaigns',
              icon: HomeIcon,
            },
          ],
        },
        {
          label: 'Campaigns',
          items: [
            {
              name: 'Manual Comms',
              to: '/app/campaigns/comms',
              icon: Megaphone,
              operatorDisabled: true, // Disabled for operator-level users
            },
            {
              name: 'AI Voice',
              to: '/app/campaigns/voice',
              icon: PhoneCall,
              operatorDisabled: true,
              comingSoon: true,
            },
            {
              name: 'Discounts',
              to: '/app/campaigns/discounts',
              icon: Percent,
              operatorDisabled: true,
              comingSoon: true,
            },
          ],
        },
      ],
    };
    return navigation;
  }

  // AI app
  if (pathname.startsWith('/app/ai')) {
    return {
      appName: 'AI',
      groups: [
        {
          items: [
            {
              name: 'Home',
              to: '/app/ai',
              icon: HomeIcon,
            },
          ],
        },
        {
          label: 'Outbound Comms',
          items: [
            { name: 'AI Workflow', to: '/app/ai/outbound/ai-workflow', icon: Target },
            { name: 'Scheduler Monitor', to: '/app/ai/outbound/scheduler', icon: Calendar },
          ],
        },
        {
          label: 'Inbound Handling',
          items: [
            { name: 'Knowledge Base', to: '/app/ai/inbound/knowledge-base', icon: BookOpen },
            { name: 'Test Chat', to: '/app/ai/inbound/test-chat', icon: MessageCircle },
            { name: 'Sessions', to: '/app/ai/inbound/sessions', icon: Activity },
            { name: 'Agent Config', to: '/app/ai/inbound/config', icon: Settings, visibility: ['system-admin'] },
            { name: 'Analytics', to: '/app/ai/inbound/analytics', icon: BarChart3, visibility: ['system-admin'] },
            { name: 'Evaluation', to: '/app/ai/inbound/evaluation', icon: ClipboardCheck, visibility: ['system-admin'] },
          ],
        },
        {
          label: 'Voice AI',
          items: [
            { name: 'Agent Configs', to: '/app/ai/voice-calls/agents', icon: Bot, comingSoon: true },
            { name: 'Test Call', to: '/app/ai/voice-calls/test', icon: PhoneOutgoing, comingSoon: true },
            { name: 'Call Records', to: '/app/ai/voice-calls/calls', icon: FileAudio, comingSoon: true },
          ],
        },
      ],
    };
  }

  if (pathname.startsWith('/app/import-export')) {
    return {
      appName: 'Config transfer',
      groups: [
        {
          items: [{ name: 'Import / Export', to: '/app/import-export', icon: ArrowRightLeft }],
        },
      ],
    };
  }

  // Library app
  if (pathname.startsWith('/app/library')) {
    return {
      appName: 'Library',
      groups: [
        {
          items: [
            { id: 'home', name: 'Home', to: '/app/library', icon: HomeIcon, activeIcon: HomeFilledIcon },
            { id: 'templates', name: 'Templates', to: '/app/library/templates', icon: FolderIcon, activeIcon: FolderFilledIcon },
            { id: 'designs', name: 'Designs', to: '/app/library/designs', icon: PaletteIcon, activeIcon: PaletteFilledIcon },
          ],
        },
      ],
    };
  }

  // Analytics app
  if (pathname.startsWith('/app/analytics')) {
    return {
      appName: 'Analytics',
      groups: [
        {
          label: 'Data',
          items: [
            { name: 'Dashboard', to: '/app/analytics/dashboard', icon: BarChart3 },
            { name: 'Performance', to: '/app/analytics/performance', icon: Target },
            { name: 'Engagement', to: '/app/analytics/engagement', icon: MessageCircle },
            {
              name: 'ODE Health Check',
              to: '/app/analytics/ode-health-check',
              icon: HeartPulse,
              requiresAllClientsAccess: true,
            },
            {
              name: 'Customer Service',
              to: '/app/analytics/customer-service',
              icon: Headset,
              requiresAllClientsAccess: true,
            },
          ],
        },
        {
          label: 'Reporting',
          items: [
            {
              name: 'Data Export',
              to: '/app/analytics/export',
              icon: Download,
              requiresAllClientsAccess: true,
            },
            {
              name: 'Explorer',
              to: '/app/analytics/explorer',
              icon: Table2,
              badge: 'Beta',
              requiresAllClientsAccess: true,
            },
            // Dashboards and Exports are tabs across the top of Explorer, not entries here:
            // two extra entries lit up Explorer as well, since its path is a prefix of theirs.
          ],
        },
      ],
    };
  }

  // Support app
  if (pathname.startsWith('/app/support')) {
    return {
      appName: 'Support',
      groups: [{ items: [{ name: 'Support Tickets', to: '/app/support', icon: LifeBuoy }] }],
    };
  }

  // Admin app (system admin only)
  if (pathname.startsWith('/app/admin')) {
    return {
      appName: 'Admin',
      groups: [
        {
          label: 'System',
          items: [
            {
              name: 'Communication Pools',
              to: '/app/admin/communication-pools',
              icon: Server,
              requiresAllClientsAccess: true,
            },
            {
              name: 'Comms Compliance',
              to: '/app/admin/compliance',
              icon: ShieldCheck,
              requiresAllClientsAccess: true,
            },
            { name: 'Governor', to: '/app/admin/governor', icon: Gauge },
            {
              name: 'Balance Lookup',
              to: '/app/admin/balance-lookup',
              icon: Wallet,
              requiresAllClientsAccess: true,
            },
            { name: 'System Costs', to: '/app/admin/system-costs', icon: Coins },
          ],
        },
      ],
    };
  }

  // Settings app
  if (pathname.startsWith('/settings')) {
    const groups: AppNavGroup[] = [
      {
        items: [
          {
            name: scopeNames.operator || 'Organisation',
            to: '/settings/organisation',
            icon: Building,
            isSelectedScope: !scopeNames.client && !scopeNames.debtType,
          },
          {
            name: scopeNames.client || 'Clients',
            to: '/settings/clients',
            icon: Building2,
            isSelectedScope: !!scopeNames.client && !scopeNames.debtType,
          },
          {
            name: scopeNames.debtType || 'Debt Types',
            to: '/settings/debt-types',
            icon: Folders,
            isSelectedScope: !!scopeNames.debtType,
          },
        ],
      },
      {
        label: 'Business',
        items: [
          { name: 'Communications', to: '/settings/communications', icon: MessageCircle },
          {
            name: 'Payments',
            to: '/settings/payments',
            icon: ReceiptEuro,
            requiresAllClientsAccess: true,
          },
          { name: 'Operations', to: '/settings/operations', icon: Settings },
        ],
      },
      {
        label: 'User Experience',
        items: [
          { name: 'Branding', to: '/settings/branding', icon: SwatchBook },
          { name: 'Customer display', to: '/settings/customer-display', icon: Rows3 },
          { name: 'Notifications', to: '/settings/notifications', icon: Bell },
          { name: 'Authentication', to: '/settings/authentication', icon: KeyRound },
        ],
      },
      {
        label: 'Commercials',
        items: [
          { name: 'Commission', to: '/settings/commission', icon: Calculator },
          { name: 'Invoicing', to: '/settings/invoicing', icon: FileText },
          { name: 'Payouts', to: '/settings/payouts', icon: Wallet },
          { name: 'Accounting', to: '/settings/accounting', icon: Book },
          { name: 'AI Model Parameters', to: '/settings/model-parameters', icon: Settings2 },
        ],
      },
      {
        label: 'Connections',
        items: [
          { name: 'SFTP', to: '/settings/sftp', icon: Server },
          { name: 'HTTP Destinations', to: '/settings/http-destinations', icon: Globe, requiresAllClientsAccess: true },
          { name: 'API Egress', to: '/settings/api-egress', icon: Send },
        ],
      },
    ];

    return {
      appName: 'Settings',
      groups,
    };
  }

  // Default: Hub navigation
  return null;
}

/**
 * Filter a flat list of items by role visibility.
 * Items without visibility are shown to everyone. system-admin sees all.
 * When multiple roles are assigned, an item is visible if any role matches.
 */
const SYSTEM_ADMIN_ROLES: readonly string[] = ['system-admin', 'admin', 'system_admin'];

function normalizeRoleList(roleOrRoles: string | readonly string[] | null | undefined): string[] {
  if (roleOrRoles == null) return [];
  if (typeof roleOrRoles === 'string') return roleOrRoles === '' ? [] : [roleOrRoles];
  return [...roleOrRoles].filter((r) => r !== '');
}

function hasSystemAdminRole(roles: readonly string[]): boolean {
  return roles.some((role) => SYSTEM_ADMIN_ROLES.includes(role));
}

export function filterItemsByRole<T extends { visibility?: NavItemVisibility }>(
  items: T[],
  roleOrRoles: string | readonly string[] | null
): T[] {
  const roles = normalizeRoleList(roleOrRoles);
  if (hasSystemAdminRole(roles)) return items;
  return items.filter((item) => !item.visibility || roles.some((role) => item.visibility?.includes(role as UserRole)));
}

/**
 * Filter navigation groups by role visibility.
 * system-admin sees all items. Items without visibility are shown to everyone.
 */
export function filterNavItemsByRole(groups: AppNavGroup[], roleOrRoles: string | readonly string[] | null): AppNavGroup[] {
  const roles = normalizeRoleList(roleOrRoles);
  if (hasSystemAdminRole(roles)) return groups;
  return groups.map((group) => ({
    ...group,
    items: filterItemsByRole(group.items, roles),
  }));
}

/**
 * Filter out items that require FGA all-clients access when the user lacks it.
 * `hasAllClientsAccess` is true when `/users/me/clients` returned `all_clients: true`.
 */
export function filterNavItemsByAllClientsAccess(groups: AppNavGroup[], hasAllClientsAccess: boolean): AppNavGroup[] {
  if (hasAllClientsAccess) return groups;
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.requiresAllClientsAccess),
    }))
    .filter((group) => group.items.length > 0);
}
